import { useCallback, useEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import { Form, FormikProvider, useFormik } from "formik";
import { isAxiosError } from "axios";
import { z } from "zod";
import { CatalogItem, CatalogItemType } from "@common/types/catalogItem";
import { CreateCatalogItemPriceOperation } from "@common/schemas/domains/catalogItem/catalogItemPrice.schema";
import { Entity } from "@common/types/entity";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { resolveAccrualLineItemDefaultAccountIds } from "@common/helpers/accrualAccount";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useAccounts } from "@hooks/useAccounts";
import { useAccrualLedgerStatus } from "@api/ledger.api";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Drawer } from "@design-system/Drawer/Drawer";
import { HStack } from "@design-system/HStack/HStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { extractApiErrorMessage } from "@api/apiError";
import {
    useCreateCatalogItem,
    useUpdateCatalogItem,
} from "@api/catalogItem.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { ClassCombobox } from "@components/transactions/TransactionSplitV2/ClassCombobox";
import { useEntities, useEntitiesById } from "@hooks/useEntities";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { CatalogItemRow } from "./catalogItems.types";
import styles from "./CatalogItemFormModal.module.scss";
import { CatalogItemFormFooter } from "./CatalogItemFormFooter";
import { useCatalogItemAccountCreate } from "./useCatalogItemAccountCreate";
import {
    CatalogItemPricesTable,
    CatalogItemPricesTableHandle,
} from "./CatalogItemPricesTable";

const itemFormSchema = z.object({
    name: z.string().trim().min(1, "Add a name").max(255),
    description: z.string().max(1000),
    classIds: z.array(z.string().uuid()),
    // Entity IDs start at 1; the form leaves this undefined until an entity is
    // picked so the schema (not an ad-hoc numeric sentinel) flags "no entity".
    entityId: z.number().int().min(1),
    // Default accounts propagated onto an invoice line's coding when this item
    // is selected. Both optional — an item need not carry either.
    revenueAccountId: z.string().uuid().nullable(),
    receivablesAccountId: z.string().uuid().nullable(),
});

type ItemFormValues = Omit<z.infer<typeof itemFormSchema>, "entityId"> & {
    entityId: number | undefined;
};

const fields = createFormikFields<ItemFormValues>();

interface Props {
    show: boolean;
    onHide: () => void;
    item?: CatalogItemRow | null;
    // Pre-fills the name field when creating a new item (e.g. the text typed in
    // the invoice line-item combobox). Ignored when editing an existing item.
    initialName?: string;
    initialDescription?: string;
    initialClassIds?: string[];
    initialRevenueAccountId?: string | null;
    initialReceivablesAccountId?: string | null;
    initialUnitAmount?: number | null;
    initialCurrency?: string;
    // Pre-fills the entity when creating a new item from a context already
    // scoped to an entity (e.g. an invoice line for a specific entity).
    // Ignored when editing an existing item.
    initialEntityId?: number;
    // Fired with the created (or matched) item after a successful create, so the
    // caller can select it (e.g. into the invoice line that triggered the modal).
    onCreated?: (item: CatalogItem) => void;
    presentation?: "modal" | "drawer";
}

const UNSAVED_CATALOG_ITEM_ID = "new-catalog-item";

function isSuggestedValue<T>(value: T, suggested: T | null | undefined) {
    return suggested != null && suggested !== "" && value === suggested;
}

export const CatalogItemFormModal: React.FC<Props> = ({
    show,
    onHide,
    item,
    initialName,
    initialDescription,
    initialClassIds: seededClassIds,
    initialRevenueAccountId,
    initialReceivablesAccountId,
    initialUnitAmount,
    initialCurrency,
    initialEntityId,
    onCreated,
    presentation = "modal",
}) => {
    const { toast, error } = useToaster();
    const createCatalogItem = useCreateCatalogItem();
    const updateCatalogItem = useUpdateCatalogItem();
    const pricesTableRef = useRef<CatalogItemPricesTableHandle>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );
    const { hasAccess: hasAccrualLedgerAccess } = useAllowFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );
    const isEditing = Boolean(item);
    const isPending =
        createCatalogItem.isPending || updateCatalogItem.isPending;
    // A new item must be scoped to a business entity. Editing can surface an
    // item already scoped to a non-business entity, so keep the full set to
    // reconcile the current value against the displayed options.
    const businessEntities = useEntities({ onlyBusiness: true });
    const entitiesById = useEntitiesById();

    const initialClassIds = useMemo(
        () => (item?.classAssignments ?? []).map((c) => c.classId),
        [item],
    );

    const defaultEntityId = useMemo(() => {
        if (item) {
            return item.entityId;
        }
        if (initialEntityId !== undefined) {
            return initialEntityId;
        }
        return businessEntities[0]?.id;
    }, [item, initialEntityId, businessEntities]);

    // Options shown in the picker: business entities plus, when editing, the
    // item's current entity if it isn't a business entity — otherwise the value
    // and the displayed list diverge (hidden picker or "Select entity"
    // placeholder despite a value being set).
    const entityOptions = useMemo<Entity[]>(() => {
        if (!item) {
            return businessEntities;
        }
        const currentEntity = entitiesById.get(item.entityId);
        if (
            currentEntity &&
            !businessEntities.some((entity) => entity.id === currentEntity.id)
        ) {
            return [currentEntity, ...businessEntities];
        }
        return businessEntities;
    }, [item, businessEntities, entitiesById]);
    const entityItems = useMemo<Array<PickerItemData<number>>>(
        () =>
            entityOptions.map((entity) => ({
                value: entity.id,
                label: entity.name ?? "",
                leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            })),
        [entityOptions],
    );

    const initialValues = useMemo<ItemFormValues>(
        () => ({
            name: item?.name ?? initialName ?? "",
            description: item?.description ?? initialDescription ?? "",
            classIds: item ? initialClassIds : (seededClassIds ?? []),
            entityId: defaultEntityId,
            revenueAccountId:
                item?.revenueAccountId ?? initialRevenueAccountId ?? null,
            receivablesAccountId:
                item?.receivablesAccountId ??
                initialReceivablesAccountId ??
                null,
        }),
        [
            item,
            initialName,
            initialDescription,
            initialClassIds,
            seededClassIds,
            defaultEntityId,
            initialRevenueAccountId,
            initialReceivablesAccountId,
        ],
    );

    const seedPriceOperations = useMemo<
        CreateCatalogItemPriceOperation[]
    >(() => {
        if (
            item ||
            !show ||
            initialUnitAmount == null ||
            !Number.isFinite(initialUnitAmount) ||
            initialUnitAmount === 0
        ) {
            return [];
        }
        return [
            {
                op: "create",
                clientId: crypto.randomUUID(),
                unitAmount: initialUnitAmount,
                currency: (initialCurrency ?? "usd").toLowerCase(),
                isDefault: true,
            },
        ];
    }, [item, show, initialUnitAmount, initialCurrency]);

    const formik = useFormik<ItemFormValues>({
        initialValues,
        enableReinitialize: true,
        validateOnMount: true,
        validate: validateFormikWithZodSchema(itemFormSchema),
        onSubmit: submitHelper({
            loading: isPending,
            handler: async (values) => {
                // Guarded by the schema (min(1)) + disabled Save button; this
                // narrows the type and is an unreachable safety net.
                if (values.entityId === undefined) {
                    return;
                }
                const name = values.name.trim();
                const description = values.description.trim() || null;
                const itemLabel = name;
                // A receivables (AR) default only makes sense for an
                // accrual-basis entity. When the selected entity is cash-only
                // the field is hidden, so its form value is never the user's
                // live choice — drop any leftover AR account so an edit can't
                // silently retain it or submit one scoped to a previous entity
                // (which the backend would reject).
                const receivablesAccountId = selectedEntityHasAccrualLedger
                    ? values.receivablesAccountId
                    : null;
                const pendingPriceOperations =
                    pricesTableRef.current?.getPendingOperations() ?? [];

                if (item) {
                    const sortClassIds = (ids: string[]) =>
                        ids.toSorted((a, b) => a.localeCompare(b));
                    const initialItemClassIds = item.classAssignments
                        ? sortClassIds(
                              item.classAssignments.map((c) => c.classId),
                          )
                        : [];
                    const didClassesChange =
                        hasClassesAccess &&
                        JSON.stringify(sortClassIds(values.classIds)) !==
                            JSON.stringify(initialItemClassIds);
                    const didEntityChange = values.entityId !== item.entityId;
                    const didRevenueAccountChange =
                        values.revenueAccountId !==
                        (item.revenueAccountId ?? null);
                    const didReceivablesAccountChange =
                        receivablesAccountId !==
                        (item.receivablesAccountId ?? null);
                    await updateCatalogItem.mutateAsync({
                        catalogItemId: item.id,
                        body: {
                            name,
                            description,
                            ...(didEntityChange
                                ? { entityId: values.entityId }
                                : {}),
                            ...(didClassesChange
                                ? { classIds: values.classIds }
                                : {}),
                            ...(didRevenueAccountChange
                                ? { revenueAccountId: values.revenueAccountId }
                                : {}),
                            ...(didReceivablesAccountChange
                                ? { receivablesAccountId }
                                : {}),
                            ...(pendingPriceOperations.length > 0
                                ? { prices: pendingPriceOperations }
                                : {}),
                        },
                        suppressErrorToast: true,
                    });
                    toast(`${itemLabel} updated`);
                    onHide();
                    return;
                }

                // In create mode the nested table has no persisted prices to
                // update/delete against, so every staged op is necessarily a
                // "create" op - this filter is a type narrowing (satisfies
                // createCatalogItemSchema's create-only `prices` batch), not
                // a runtime guard against real update/delete ops appearing
                // here. Lets a new item start with several prices (e.g.
                // several currencies) in one request, same as an existing
                // item can stage several price creates before an update Save.
                const createPriceOperations: CreateCatalogItemPriceOperation[] =
                    pendingPriceOperations.filter(
                        (
                            operation,
                        ): operation is CreateCatalogItemPriceOperation =>
                            operation.op === "create",
                    );
                const result = await createCatalogItem.mutateAsync({
                    body: {
                        name,
                        type: CatalogItemType.SERVICE,
                        currency: "usd",
                        description,
                        ...(createPriceOperations.length > 0
                            ? { prices: createPriceOperations }
                            : {}),
                        entityId: values.entityId,
                        ...(hasClassesAccess && values.classIds.length > 0
                            ? { classIds: values.classIds }
                            : {}),
                        ...(values.revenueAccountId
                            ? { revenueAccountId: values.revenueAccountId }
                            : {}),
                        ...(receivablesAccountId
                            ? { receivablesAccountId }
                            : {}),
                    },
                    suppressErrorToast: true,
                });
                if (result.created) {
                    toast(`${itemLabel} created`);
                } else {
                    toast(`${itemLabel} already exists`);
                }
                onCreated?.(result.item);
                onHide();
            },
            failureHandler: async (err, values) => {
                const itemLabel = values.name.trim() || "the item";
                const fallback = item
                    ? `Failed to update ${itemLabel}`
                    : "Failed to create item";
                error(
                    isAxiosError(err)
                        ? extractApiErrorMessage(err.response?.data, fallback)
                        : fallback,
                );
            },
        }),
    });

    // Accounts are entity-scoped; loaded against the form's *currently
    // selected* entity (not just the initial default) so the two account
    // comboboxes can resolve the selected account if the user changes the
    // entity picker before picking an account.
    const { accounts, isLoading: isLoadingAccounts } = useAccounts({
        entityId: formik.values.entityId,
    });
    // Fails open while the ledger query loads/errors so a load failure never
    // makes an accrual entity look cash-only (which would hide Receivables and
    // clear a valid AR default).
    const {
        hasAccrualLedger: selectedEntityHasAccrualLedger,
        isLoading: isLoadingLedgers,
    } = useAccrualLedgerStatus(formik.values.entityId);
    const showReceivablesAccount =
        hasAccrualLedgerAccess && selectedEntityHasAccrualLedger;
    const showAccountNumbers = useShowAccountNumbers();

    // Reset on open so edits discarded via Cancel don't reappear: the modal
    // stays mounted for the close animation, so Formik state would otherwise
    // persist across open/close cycles. resetForm is identity-stable.
    const { resetForm, setFieldValue } = formik;
    // Tracks which entity's defaults were last applied to the two account
    // fields, so a given entity's defaults are applied at most once per
    // mount/open (further manual edits by the user aren't clobbered by
    // re-renders) while still re-applying on every actual entity change.
    const appliedDefaultsEntityIdRef = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (show) {
            resetForm();
            appliedDefaultsEntityIdRef.current = undefined;
        }
    }, [show, resetForm]);

    const selectedEntity = entityOptions.find(
        (entity) => entity.id === formik.values.entityId,
    );
    // Narrowed via the found Entity (not a cast) so the account comboboxes
    // below get a definite number without `as number`.
    const selectedFormEntityId = selectedEntity?.id;
    const setAccountId = useCallback(
        (
            field: "revenueAccountId" | "receivablesAccountId",
            accountId: string,
        ) => {
            void setFieldValue(field, accountId);
        },
        [setFieldValue],
    );
    const {
        createAccountModal,
        isCreatingAccount,
        openRevenueAccountCreate,
        openReceivablesAccountCreate,
    } = useCatalogItemAccountCreate({
        entityId: selectedFormEntityId,
        setAccountId,
    });

    // New items are preselected with the income (revenue) default for the
    // *currently selected* entity — accounts are entity-scoped, so switching
    // entities must swap in that entity's own default rather than leaving the
    // previous entity's account selected. Receivables is only filled when the
    // selected entity actually has an accrual ledger (and the plan feature is
    // on); otherwise it is cleared so a leftover AR account from a previous
    // entity is not submitted. Scoped to create mode only: editing an existing
    // item must never override the persisted account choice. Depends on `show`
    // because the modal stays mounted while closed; resetForm on open would
    // otherwise wipe defaults and this effect would not rerun. Runs once per
    // distinct entity per open (guarded by the ref above), and only once that
    // entity's accounts and ledgers have actually loaded so it doesn't fire
    // against a stale/empty list. The empty-accounts guard also means a failed
    // load (isLoadingAccounts flips false with no data) never marks the entity
    // handled, so defaults still apply once a retry/invalidation delivers the
    // accounts. Ledgers must be loaded too — otherwise a cash-only entity
    // would be marked handled before we know it has no accrual ledger.
    useEffect(() => {
        if (
            !show ||
            isEditing ||
            selectedFormEntityId === undefined ||
            isLoadingAccounts ||
            isLoadingLedgers ||
            accounts.length === 0 ||
            appliedDefaultsEntityIdRef.current === selectedFormEntityId
        ) {
            return;
        }
        appliedDefaultsEntityIdRef.current = selectedFormEntityId;
        const defaults = resolveAccrualLineItemDefaultAccountIds({
            mode: "invoices",
            accounts,
        });
        const keepsLineAccounts = selectedFormEntityId === initialEntityId;
        const revenueAccountId =
            keepsLineAccounts && initialRevenueAccountId
                ? initialRevenueAccountId
                : defaults.accountId;
        let receivablesAccountId: string | null = null;
        if (showReceivablesAccount) {
            receivablesAccountId =
                keepsLineAccounts && initialReceivablesAccountId
                    ? initialReceivablesAccountId
                    : defaults.accrualAccountId;
        }
        setFieldValue("revenueAccountId", revenueAccountId);
        setFieldValue("receivablesAccountId", receivablesAccountId);
    }, [
        show,
        isEditing,
        showReceivablesAccount,
        selectedFormEntityId,
        initialEntityId,
        initialRevenueAccountId,
        initialReceivablesAccountId,
        isLoadingAccounts,
        isLoadingLedgers,
        accounts,
        setFieldValue,
    ]);

    // A new item must be scoped to a business entity. With none eligible the
    // entity picker is hidden and Save stays disabled forever, so surface an
    // explicit empty state instead of an un-submittable form. Editing is
    // unaffected — the existing item already carries an entity.
    const hasNoEligibleEntity = !isEditing && businessEntities.length === 0;

    const isSuggested = {
        name: isSuggestedValue(formik.values.name, initialName),
        description: isSuggestedValue(
            formik.values.description,
            initialDescription,
        ),
        classIds: isSuggestedValue(
            formik.values.classIds.toSorted().join(","),
            seededClassIds?.toSorted().join(","),
        ),
        revenueAccountId: isSuggestedValue(
            formik.values.revenueAccountId,
            initialRevenueAccountId,
        ),
        receivablesAccountId: isSuggestedValue(
            formik.values.receivablesAccountId,
            initialReceivablesAccountId,
        ),
        entityId: isSuggestedValue(formik.values.entityId, initialEntityId),
    };

    const title = isEditing ? "Edit item" : "Add item";
    const content = (
        <>
            {hasNoEligibleEntity ? (
                <EmptyState
                    header="No business entity available"
                    body="Add a business entity before creating items — every item must belong to one."
                    ctaText="Close"
                    action={onHide}
                />
            ) : (
                <FormikProvider value={formik}>
                    <Form
                        onSubmit={formik.handleSubmit}
                        className={classNames({
                            [styles.drawerForm]: presentation === "drawer",
                        })}
                    >
                        <div
                            className={classNames({
                                [styles.drawerBody]: presentation === "drawer",
                            })}
                        >
                            <fields.Text
                                name="name"
                                label="Item name"
                                placeholder="Add item name"
                                size="xl"
                                ref={nameInputRef}
                                className={classNames(styles.textField, {
                                    [styles.suggested]: isSuggested.name,
                                })}
                            />
                            <fields.Text
                                name="description"
                                label="Item description"
                                placeholder="Add item description"
                                size="xl"
                                className={classNames(styles.textField, {
                                    [styles.suggested]: isSuggested.description,
                                })}
                            />
                            <CatalogItemPricesTable
                                ref={pricesTableRef}
                                catalogItemId={
                                    item?.id ?? UNSAVED_CATALOG_ITEM_ID
                                }
                                prices={item?.prices}
                                currency={
                                    item?.currency ?? initialCurrency ?? "usd"
                                }
                                isOpen={show}
                                isSaving={isPending}
                                initialOperations={seedPriceOperations}
                            />
                            {hasClassesAccess && (
                                <FormikField<string[]>
                                    name="classIds"
                                    label="Class"
                                    control="button"
                                    className={classNames(styles.buttonField, {
                                        [styles.suggested]:
                                            isSuggested.classIds,
                                    })}
                                >
                                    {({ helpers, field }) => (
                                        <ClassCombobox
                                            selectedClassIds={field.value}
                                            onChange={(classIds) => {
                                                void helpers.setValue(classIds);
                                            }}
                                            triggerButtonProps={{
                                                size: "xl",
                                                variant: "primary",
                                            }}
                                        />
                                    )}
                                </FormikField>
                            )}
                            {selectedFormEntityId !== undefined && (
                                <fields.LedgerAccount
                                    name="revenueAccountId"
                                    label="Income account"
                                    className={classNames(styles.buttonField, {
                                        [styles.suggested]:
                                            isSuggested.revenueAccountId,
                                    })}
                                    entityId={selectedFormEntityId}
                                    showAccountCode={showAccountNumbers}
                                    onCreateAccount={openRevenueAccountCreate}
                                    clearable
                                    emptyValue={null}
                                    buttonProps={{ size: "xl" }}
                                />
                            )}
                            {selectedFormEntityId !== undefined &&
                                showReceivablesAccount && (
                                    <fields.LedgerAccount
                                        name="receivablesAccountId"
                                        label="Receivables account"
                                        className={classNames(
                                            styles.buttonField,
                                            {
                                                [styles.suggested]:
                                                    isSuggested.receivablesAccountId,
                                            },
                                        )}
                                        entityId={selectedFormEntityId}
                                        showAccountCode={showAccountNumbers}
                                        allowedAccountTypes={[
                                            AccountType.accountsReceivable,
                                        ]}
                                        onCreateAccount={
                                            openReceivablesAccountCreate
                                        }
                                        clearable
                                        emptyValue={null}
                                        buttonProps={{ size: "xl" }}
                                    />
                                )}
                            {entityOptions.length > 1 && (
                                <FormikField<number | undefined>
                                    name="entityId"
                                    label="Entity"
                                    control="button"
                                    className={classNames(styles.buttonField, {
                                        [styles.suggested]:
                                            isSuggested.entityId,
                                    })}
                                >
                                    {({ helpers }) => (
                                        <Picker.Select
                                            items={entityItems}
                                            searchable
                                            searchPlaceholder="Search entities"
                                            emptyLabel="No matching entities"
                                            value={selectedEntity?.id ?? null}
                                            onValueChange={(entityId) => {
                                                if (entityId !== null) {
                                                    void helpers.setValue(
                                                        entityId,
                                                    );
                                                }
                                            }}
                                            placeholder="Select entity"
                                            renderValue={() =>
                                                selectedEntity ? (
                                                    <HStack
                                                        gap={8}
                                                        align="center"
                                                    >
                                                        <EntityAvatar
                                                            entity={
                                                                selectedEntity
                                                            }
                                                            size="xs"
                                                            aria-hidden
                                                        />
                                                        <span>
                                                            {
                                                                selectedEntity.name
                                                            }
                                                        </span>
                                                    </HStack>
                                                ) : undefined
                                            }
                                            trigger={
                                                <Picker.Button
                                                    variant="primary"
                                                    size="xl"
                                                />
                                            }
                                        />
                                    )}
                                </FormikField>
                            )}
                        </div>
                        <CatalogItemFormFooter
                            item={item}
                            isPending={isPending}
                            isValid={formik.isValid}
                            onHide={onHide}
                            presentation={presentation}
                        />
                    </Form>
                </FormikProvider>
            )}
            {createAccountModal}
        </>
    );

    if (presentation === "drawer") {
        return (
            <Drawer
                open={show}
                onClose={onHide}
                direction="right"
                size="half"
                noPadding
                noScroll
                headerAddonLeft={title}
                initialFocus={nameInputRef}
                dismissible={!isCreatingAccount}
            >
                {content}
            </Drawer>
        );
    }

    return (
        <PlainModal
            show={show}
            onHide={onHide}
            size="lg"
            initialFocus={nameInputRef}
            backdrop={isCreatingAccount ? "static" : true}
        >
            <StandardModalHeader>{title}</StandardModalHeader>
            {content}
        </PlainModal>
    );
};
